/**
 * Creates a form validator instance.
 *
 * The form validator is a utility function that validates a form state object against
 * a set of predefined validation rules. It takes a form state object, a form element,
 * and an optional error messages object as its arguments. The form state object is
 * expected to have the same structure as the form element, with each field represented
 * as a key-value pair.
 *
 * @param {Object} form - The form state object to validate.
 * @param {HTMLElement} formEl - The form element to scan for fields.
 * @param {Object} [errorMessages={}] - An optional object of error messages to use.
 * @returns {Object} A validator API with the following methods:
 *   validate(): boolean - Validates the entire form and returns true if all fields are valid, false otherwise.
 *   validateField(string): boolean - Validates a single field and returns true if the field is valid, false otherwise.
 *   registerField(string, string): void - Registers a field with a specified type in the validator.
 *   debugFields(): void - A utility method to inspect the field types registered in the validator.
 */
export function createFormValidator(form, formEl, errorMessages = {}) {
    const fieldTypes = {};
    const autoGeneratedErrorMessages = {};

    // Map of type-specific validators
    // kKeys must be what you assigned to data-type or type
    const typeValidators = {
        text: (value) => String(value).trim() !== '',
        postcode: validatePostcode,
        email: validateEmail,
        url: validateURL,
        number: (value) => !isNaN(Number(value)),
        currency: validateCurrency,
        date: validateDate
    };

    // Default error messages for type validation
    function getDefaultTypeErrorMessage(type) {
        const messages = {
            postcode: 'Postcode format is invalid.',
            email: 'Email format is invalid.',
            url: 'Website format is invalid.',
            number: 'Value must be a number.',
            currency: 'Please enter a valid amount.',
            date: 'Please enter a valid date.',
            text: 'Please enter a value.'
        };
        return messages[type] || 'Invalid value.';
    }

    if (!form) {
        throw new Error(
            'Attempting to make a form validator without a form state object. Form state object is required to display errors properly. \n Please pass in a form state object. this.<YourFormName>'
        );
    }

    if (!formEl) {
        throw new Error(
            'Attempting to make a form validator without a form element. Form element is required to scan the DOM. \n Please pass in a form element. this.$refs.<refNameForForm>'
        );
    }

    // Auto-detect types from DOM inputs
    const inputs = formEl.querySelectorAll('[name], [x-model], [x-bind\\:model], [data-model]');
    // console.log('[Validator] Inputs found:', inputs);

    inputs.forEach((input) => {
        // console.log('[Validator] Input:', input.outerHTML);

        // Try to get the Alpine model binding from x-model
        const modelAttr = input.getAttribute('x-model') || input.getAttribute(':model');
        const nameAttr = input.getAttribute('name');

        let modelField = null;

        // If the x-model is found, strip it off so we can use it to access the form data
        if (modelAttr && modelAttr.startsWith('form.')) {
            modelField = modelAttr.replace(/^form\./, '');
        }

        // Fallback to name attribute if x-model is not usable
        const fieldKey = modelField || nameAttr;

        // Couldn't find a valid field key
        //IE no valid x-model (form.*) or name attribute
        // on the input from the form
        if (!fieldKey) {
            console.warn(
                `Unable to determine field key from input. ` +
                    `No valid x-model (form.*) or name attribute found. Check this input:`,
                input
            );
            return;
        }

        // In case theres an x-model present in the dom for the form,
        // and cant find a matching key in the form object state you passed in
        if (!Object.prototype.hasOwnProperty.call(form, fieldKey)) {
            console.warn(
                `Field "${fieldKey}" is in the DOM but not in your form object. ` +
                    `Using the ${modelField ? 'x-model' : 'name'} attribute from DOM. ` +
                    `Ensure your form data object has a key called "${fieldKey}".`
            );
        }

        // To extract data-type="postcode" stuff
        const customType = input.dataset.type;
        // To extract type="email" stuff
        const nativeType = input.getAttribute('type');
        let resolvedType = customType || nativeType || 'text';

        // Support multiple rules like `currency|required`
        const rules = resolvedType.split('|').map((rule) => rule.trim());

        // Build allowed rules from existing validators + required
        const validRules = [...Object.keys(typeValidators), 'required'];
        const unknownRules = rules.filter((rule) => !validRules.includes(rule));
        if (unknownRules.length > 0) {
            throw new Error(
                `Unknown validation rule(s) "${unknownRules.join(', ')}" for field "${fieldKey}".\n\n` +
                    `Supported rules: ${validRules.join(', ')}.`
            );
        }

        let isRequired = rules.includes('required');

        // Pick the first non-required rule as the type
        resolvedType = rules.find((rule) => rule !== 'required') || 'text';

        // No type attr or data-type attr found
        if (!resolvedType) {
            throw new Error(
                `Missing data-type or type attribute for field "${fieldKey}".\n\n` +
                    `Input snippet:\n\n${input.outerHTML.slice(0, 200)}`
            );
        }

        // Unsupported type, create a validation rule for it
        // In the type validator const.
        if (!typeValidators[resolvedType]) {
            throw new Error(
                `Unsupported field type "${resolvedType}" for field "${fieldKey}". ` +
                    `Supported types: ${Object.keys(typeValidators).join(', ')}.\n\n` +
                    `Input snippet:\n\n${input.outerHTML.slice(0, 200)}`
            );
        }

        // console.log('[Validator] Field:', fieldKey, 'Type:', resolvedType);

        // Register the fields validator rule
        fieldTypes[fieldKey] = resolvedType;

        // Auto-register required fields
        if (isRequired || input.hasAttribute('required') || input.dataset.required === 'true') {
            autoGeneratedErrorMessages[fieldKey] = {
                required: errorMessages.required || getDefaultTypeErrorMessage('text')
            };
        }
    });

    // Validate a single field
    function validateField(field) {
        const value = form[field];
        const type = fieldTypes[field];
        let hasErrors = false;

        // Determine if empty
        const isEmpty = value === null || value === undefined || String(value).trim() === '';

        // Merge error messages: explicit > auto-detected
        const fieldErrors = errorMessages?.[field] || autoGeneratedErrorMessages?.[field];

        // Use useIf from the original fieldErrors object
        const hasUseIf = typeof fieldErrors === 'object' && typeof fieldErrors.useIf === 'function';
        const shouldValidate = !hasUseIf || fieldErrors.useIf();

        // Skip validation if useIf returns false
        if (!shouldValidate) return;

        // Normalize error messages (allowing string or object format)
        const messages = {
            required: typeof fieldErrors === 'string' ? fieldErrors : fieldErrors?.required,
            invalid: fieldErrors?.invalid
        };

        // Determine if required based on presence of required message
        const isRequired = messages.required !== undefined;
        if (isRequired && isEmpty) {
            form.errors[field] = messages.required || 'This field is required';
            hasErrors = true;
        }

        // Type-specific validation (only if not empty)
        if (!isEmpty && type && typeValidators[type] && !typeValidators[type](value)) {
            form.errors[field] = messages.invalid || getDefaultTypeErrorMessage(type);
            hasErrors = true;
        }

        // Clear existing error if field is valid
        if (!hasErrors) {
            delete form.errors[field];
        }
    }

    // Validate all fields
    function validate() {
        form.errors = {};
        let hasErrors = false;

        for (const field in fieldTypes) {
            validateField(field);
            if (form.errors[field]) hasErrors = true;
        }

        return !hasErrors;
    }

    // Validation helpers
    function validatePostcode(postCode) {
        postCode = postCode.replace(/\s/g, '');
        const regex = /^[A-Z]{1,2}[0-9]{1,2}[A-Z]?\s?[0-9][A-Z]{2}$/i;
        return regex.test(postCode);
    }

    function validateEmail(email) {
        const regex =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}(\.[0-9]{1,3}){3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return regex.test(String(email).toLowerCase());
    }

    /**
     * Validates a currency string.
     *
     * The function strips currency symbols and whitespace, and checks if the resulting string
     * matches the following format: digits, optional . and 1 or 2 decimal places.
     *
     * @param {string|number} value - The currency string to validate.
     * @returns {boolean} - Returns true if the currency is valid, false otherwise.
     */
    function validateCurrency(value) {
        if (typeof value !== 'string') value = String(value);
        const cleaned = value.replace(/£/g, '').trim();
        const regex = /^\d+(\.\d{1,2})?$/;
        return regex.test(cleaned);
    }

    /**
     * Validates a date string against known formats.
     *
     * Supported formats:
     * - DD-MM-YYYY or DD/MM/YYYY
     * - YYYY-MM-DD
     *
     * The function checks if the input string matches one of the supported date formats,
     * normalizes the separators to dashes, and verifies the constructed date's validity.
     *
     * @param {string} value - The date string to validate.
     * @returns {boolean} - Returns true if the date is valid and matches a known format, false otherwise.
     */
    function validateDate(value) {
        if (typeof value !== 'string') return false;

        // Try to match known formats
        const formats = [
            /^\d{2}-\d{2}-\d{4}$/, // DD-MM-YYYY
            /^\d{2}\/\d{2}\/\d{4}$/, // DD/MM/YYYY
            /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, // ISO datetime without seconds
            /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/ // ISO datetime with seconds
        ];

        const matchedFormat = formats.find((regex) => regex.test(value));
        if (!matchedFormat) return false;

        // Normalize separators for slash-based date
        const normalized = value.replace(/\//g, '-');

        let year, month, day;

        if (/^(\d{4})-(\d{2})-(\d{2})T/.test(normalized)) {
            // ISO datetime
            [year, month, day] = normalized.split('T')[0].split('-').map(Number);
        } else if (/^\d{4}-/.test(normalized)) {
            // YYYY-MM-DD
            [year, month, day] = normalized.split('-').map(Number);
        } else {
            // DD-MM-YYYY
            [day, month, year] = normalized.split('-').map(Number);
        }

        // Use numeric constructor to avoid JS Date quirks
        const date = new Date(year, month - 1, day);
        return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
    }

    /**
     * Validates a URL string.
     *
     * The function attempts to create a URL object from the input string and returns
     * true if the URL is valid, false otherwise.
     *
     * @param {string} url - The URL string to validate.
     * @returns {boolean} - Returns true if the URL is valid, false otherwise.
     */
    function validateURL(url) {
        try {
            new URL(url);
            return true;
        } catch (_) {
            return false;
        }
    }

    /**
     * Registers a field with a specified type in the validator.
     *
     * This function allows you to manually register a field and its expected data type,
     * even if the field is not present in the DOM at page load. Great for x-if or dynamic fields.
     * The provided type must be one of the supported types defined in the typeValidators object.
     *
     * @param {string} field - The name of the field to register.
     * @param {string} [type='text'] - The expected type of the field's value.
     * @throws {Error} If the specified type is not supported by the validator.
     */

    function registerField(field, type = 'text') {
        if (!typeValidators[type]) {
            throw new Error(`Validator: Tried to register field "${field}" with unsupported type "${type}"`);
        }
        fieldTypes[field] = type;
    }

    // Utility: Debug method to inspect field types (optional)
    function debugFields() {
        return console.log({ ...fieldTypes });
    }

    function stripPoundSign(value) {
        return parseFloat(String(value).replace(/£/g, '').trim());
    }

    function addPoundSign(value) {
        value = String(value).trim();
        return value.startsWith('£') ? value : `£${value}`;
    }

    /**
     * Format a currency field value.
     *
     * Strips any "£" symbols, parses the value as a float, and formats it as a string with two decimal places
     * and a "£" symbol at the start. If the value is not a valid currency, it is left unchanged.
     *
     * @param {string} field - The name of the field to format.
     * @return {string} The formatted value of the field.
     */
    function validateAndFormatCurrency(field) {
        const value = form[field];

        // Validate first using the same logic as currency rule
        if (!validateCurrency(value)) {
            return value;
        }

        const parsed = stripPoundSign(value);
        form[field] = addPoundSign(parsed.toFixed(2));
    }

    // Return validator API
    return {
        validate,
        validateField,
        registerField,
        stripPoundSign,
        addPoundSign,
        validateAndFormatCurrency,
        debugFields
    };
}
